(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else {
    root.RuneGiftOfferController = api.createController(root);
    if (root.document.readyState === 'loading') root.document.addEventListener('DOMContentLoaded', () => root.RuneGiftOfferController.start(), { once: true });
    else root.RuneGiftOfferController.start();
  }
})(typeof window === 'undefined' ? null : window, function () {
  'use strict';
  const defaults = {
    enabled: false,
    thresholdCents: 29700,
    currency: 'USD',
    giftProductId: 4483470426211,
    giftPropertyKeys: ['_rune_gift'],
    excludedProductIds: [],
    excludedVariantIds: [],
    ghostProductTypes: ['giftbox_ghost_product'],
    couponEnabled: true,
    requirePicker: false,
    recommendationUrl: '/products/lume-stacker-slim-lume-bands',
    pickerSelector: null,
    pickerEvent: null,
    pickerEventReady: false,
    refreshEvents: ['product:added', 'cart:refresh', 'rune:giftbox-updated'],
    debounceMs: 220,
    text: {
      heading: 'Free Blue Water Apollo at $297',
      finePrint: 'After discounts · One per order · Shipping and tax excluded.',
      remaining: 'Add {amount} for your free ring.',
      unlocked: 'Your free ring is unlocked.',
      qualified: 'You have reached the $297 spending requirement.',
      selected: 'Your free gift is in your cart.',
      checking: 'Updating your gift offer…',
      picker: 'Choose your ring size',
      manage: 'Choose your gift size',
      recommendation: 'Explore Lume Stackers',
      pickerError: 'Please use the gift selector in your cart.',
      progress: 'Progress toward your free ring',
      codeLabel: 'Discount code',
      codeApply: 'Apply',
      codeRemove: 'Remove codes',
      codeApplied: 'Discount code applied.',
      codeNotApplied: 'This code could not be applied to your current cart.',
      codeError: 'We could not update your code. Please try again.',
      codeUnavailable: 'Please apply your discount code at checkout.',
      codeHelp: 'Existing codes are kept. Shopify applies eligible combinations.',
      codesLabel: 'Applied codes: {codes}',
      codesRemoved: 'Discount codes removed.'
    }
  };
  const cents = value => Number.isSafeInteger(value) && value >= 0;
  const merge = config => ({ ...defaults, ...config, text: { ...defaults.text, ...(config && config.text) } });
  const truthyProperty = value => value !== undefined && value !== null && value !== false && value !== '' && value !== 'false' && value !== '0';
  function designatedGift(item, config) {
    if ((config.ghostProductTypes || []).includes(item.product_type)) return true;
    if ((config.excludedProductIds || []).map(String).includes(String(item.product_id))) return true;
    if ((config.excludedVariantIds || []).map(String).includes(String(item.variant_id || item.id))) return true;
    if ((config.giftPropertyKeys || []).some(key => truthyProperty((item.properties || {})[key]))) return true;
    return typeof config.isGiftLine === 'function' && config.isGiftLine(item) === true;
  }
  function enteredCodes(cart, activeOnly) {
    if (!Array.isArray(cart.discount_codes)) return null;
    const result = [];
    for (const entry of cart.discount_codes) {
      if (!entry || typeof entry.code !== 'string') return null;
      if (activeOnly && entry.applicable === false) continue;
      const code = entry.code.trim();
      if (code && !result.some(value => value.toLowerCase() === code.toLowerCase())) result.push(code);
    }
    return result;
  }
  const activeCodes = cart => enteredCodes(cart, true);
  function discountPayload(cart, action) {
    const existing = enteredCodes(cart, false);
    if (existing === null) throw new Error('discount-codes-unavailable');
    if (action.clear === true) return { discount: '' };
    const code = String(action.code || '').trim();
    if (!code || code.includes(',') || code.length > 128) throw new Error('invalid-discount-code');
    if (!existing.some(value => value.toLowerCase() === code.toLowerCase())) existing.push(code);
    return { discount: existing.join(',') };
  }
  function model(cart, options) {
    const config = merge(options);
    const unknown = { ready: false, eligible: false, selected: false, qualifyingCents: 0, remainingCents: config.thresholdCents, progress: 0 };
    if (!cart || !Array.isArray(cart.items) || !cents(cart.total_price) || !cents(config.thresholdCents) || config.thresholdCents === 0 || cart.currency !== config.currency) return unknown;
    const excluded = cart.items.filter(item => designatedGift(item, config));
    let excludedCents = 0;
    if (excluded.length) {
      const linePriceValid = cart.items.every(item => cents(item.line_price));
      const finalPriceValid = cart.items.every(item => cents(item.final_line_price));
      const reconciles = key => cart.items.reduce((sum, item) => sum + item[key], 0) === cart.total_price;
      const key = linePriceValid && reconciles('line_price') ? 'line_price' : finalPriceValid && reconciles('final_line_price') ? 'final_line_price' : null;
      if (key) excludedCents = excluded.reduce((sum, item) => sum + item[key], 0);
      else if (excluded.every(item => item.line_price === 0 && item.final_line_price === 0)) excludedCents = 0;
      else return unknown;
    }
    const qualifyingCents = Math.max(0, cart.total_price - excludedCents);
    return {
      ready: true,
      eligible: qualifyingCents >= config.thresholdCents,
      selected: excluded.some(item => item.quantity > 0),
      qualifyingCents,
      excludedCents,
      remainingCents: Math.max(0, config.thresholdCents - qualifyingCents),
      progress: Math.min(100, Math.max(0, qualifyingCents / config.thresholdCents * 100))
    };
  }
  function latestLoader(read, commit, fail) {
    let generation = 0;
    let aborter = null;
    return {
      invalidate() { generation += 1; if (aborter) aborter.abort(); },
      async run() {
        const current = ++generation;
        if (aborter) aborter.abort();
        aborter = new AbortController();
        try {
          const value = await read(aborter.signal);
          if (generation === current) commit(value);
        } catch (error) {
          if (generation === current && error.name !== 'AbortError') fail(error);
        }
      }
    };
  }
  function createController(win) {
    const doc = win.document;
    let config = merge(win.RuneGiftOffer || {});
    let cart = null;
    let current = model(null, config);
    let running = false;
    let timer = null;
    let observer = null;
    let writing = false;
    let message = '';
    let listeners = [];
    let mounted = new Map();
    let version = 0;
    let couponExpanded = false;
    const route = suffix => (win.Shopify && win.Shopify.routes && win.Shopify.routes.root || '/').replace(/\/?$/, '/') + suffix;
    const readCart = async signal => {
      const response = await win.fetch(route('cart.js'), { credentials: 'same-origin', cache: 'no-store', signal, headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('cart-unavailable');
      return response.json();
    };
    const loader = latestLoader(readCart, value => {
      if (!running || !config.enabled || writing) return;
      cart = value;
      current = model(value, config);
      render();
      doc.dispatchEvent(new win.CustomEvent('rune:gift-cart-read', { detail: { cart: value } }));
    }, () => { current = model(null, config); render(); });
    const money = value => new Intl.NumberFormat(doc.documentElement.lang || 'en-US', { style: 'currency', currency: config.currency }).format(value / 100);
    const replace = (text, key, value) => text.replace('{' + key + '}', value);
    function element(tag, className, text) {
      const node = doc.createElement(tag);
      if (className) node.className = className;
      if (text !== undefined) node.textContent = text;
      return node;
    }
    function pickerTarget() {
      if (!config.pickerSelector) return null;
      try { return Array.from(doc.querySelectorAll(config.pickerSelector)).find(node => !node.closest('[data-rune-gift-offer]')); }
      catch (_) { return null; }
    }
    function pickerReady() {
      if (typeof config.isPickerReady === 'function') {
        try { if (!config.isPickerReady()) return false; }
        catch (_) { return false; }
      }
      return typeof config.openPicker === 'function' || !!pickerTarget() || !!(config.pickerEvent && config.pickerEventReady);
    }
    async function openPicker() {
      if (!current.ready || !current.eligible || writing) return;
      try {
        if (typeof config.openPicker === 'function') await config.openPicker({ cart, offer: { ...current } });
        else if (pickerTarget()) pickerTarget().click();
        else if (config.pickerEvent && config.pickerEventReady) {
          const accepted = !doc.dispatchEvent(new win.CustomEvent(config.pickerEvent, { cancelable: true, detail: { cart, offer: { ...current } } }));
          if (!accepted) throw new Error('picker-not-connected');
        } else throw new Error('picker-not-connected');
      } catch (_) { message = config.text.pickerError; render(); }
    }
    async function updateDiscount(action) {
      if (writing || !running || !config.enabled) return;
      writing = true;
      const activeVersion = version;
      const previousFocus = doc.activeElement;
      loader.invalidate();
      current = model(null, config);
      message = '';
      render();
      try {
        const before = await readCart();
        if (activeVersion !== version || !running || !config.enabled) return;
        const payload = discountPayload(before, action);
        const response = await win.fetch(route('cart/update.js'), { credentials: 'same-origin', method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(payload) });
        if (!response.ok) throw new Error('discount-update-failed');
        const updated = await response.json();
        if (activeVersion !== version || !running || !config.enabled) return;
        loader.invalidate();
        cart = updated;
        current = model(updated, config);
        doc.dispatchEvent(new win.CustomEvent('rune:gift-cart-read', { detail: { cart: updated } }));
        const codes = activeCodes(updated);
        message = action.clear ? config.text.codesRemoved : codes && codes.some(value => value.toLowerCase() === action.code.trim().toLowerCase()) ? config.text.codeApplied : config.text.codeNotApplied;
        doc.dispatchEvent(new win.CustomEvent('cart:refresh', { bubbles: true }));
      } catch (error) {
        if (activeVersion === version) message = error.message === 'discount-codes-unavailable' ? config.text.codeUnavailable : config.text.codeError;
      } finally {
        writing = false;
        if (running && config.enabled) {
          render();
          if (previousFocus && previousFocus.isConnected && typeof previousFocus.focus === 'function' && doc.activeElement === doc.body) previousFocus.focus({ preventScroll: true });
          schedule();
        }
      }
    }
    function build() {
      const box = element('section', 'RuneGiftOffer');
      box.setAttribute('data-rune-gift-offer', '');
      const heading = element('h3', 'RuneGiftOffer__Title');
      const status = element('p', 'RuneGiftOffer__Status');
      status.setAttribute('aria-live', 'polite');
      const progress = element('progress', 'RuneGiftOffer__Progress');
      progress.max = 100;
      const fine = element('p', 'RuneGiftOffer__Fine');
      const picker = element('button', 'RuneGiftOffer__Picker');
      picker.type = 'button';
      picker.addEventListener('click', openPicker);
      const recommendation = element('a', 'RuneGiftOffer__Recommendation');
      const coupon = element('details', 'RuneGiftOffer__Coupon');
      coupon.open = couponExpanded;
      const summary = element('summary', 'RuneGiftOffer__CodeSummary');
      summary.addEventListener('click', () => { couponExpanded = !coupon.open; });
      coupon.addEventListener('toggle', () => { couponExpanded = coupon.open; });
      const couponBody = element('div', 'RuneGiftOffer__CouponBody');
      const label = element('label', 'RuneGiftOffer__CodeLabel');
      const input = element('input', 'RuneGiftOffer__CodeInput');
      input.type = 'text';
      input.autocomplete = 'off';
      input.maxLength = 128;
      label.append(input);
      const apply = element('button', 'RuneGiftOffer__Apply');
      apply.type = 'button';
      const submit = () => { const code = input.value.trim(); if (code) updateDiscount({ code }); };
      apply.addEventListener('click', submit);
      input.addEventListener('keydown', event => { if (event.key === 'Enter') { event.preventDefault(); submit(); } });
      const codes = element('p', 'RuneGiftOffer__Codes');
      const remove = element('button', 'RuneGiftOffer__Remove');
      remove.type = 'button';
      remove.addEventListener('click', () => updateDiscount({ clear: true }));
      const help = element('p', 'RuneGiftOffer__Fine');
      const notice = element('p', 'RuneGiftOffer__Notice');
      notice.setAttribute('role', 'status');
      couponBody.append(label, apply, codes, remove, help, notice);
      coupon.append(summary, couponBody);
      box.append(heading, status, progress, fine, picker, recommendation, coupon);
      return { box, heading, status, progress, fine, picker, recommendation, coupon, summary, label, input, apply, codes, remove, help, notice };
    }
    function anchors() {
      const nodes = Array.from(doc.querySelectorAll('[data-rune-gift-mount]')).map(node => ({ node, append: true }));
      for (const rootSelector of ['#sidebar-cart', '#shopify-section-cart-template']) {
        const root = doc.querySelector(rootSelector);
        if (!root || nodes.some(anchor => root.contains(anchor.node))) continue;
        const node = root.querySelector('.Cart__Checkout, .Cart__Recap, .Cart__ShippingNotice, .EmptyState__Action');
        if (node) nodes.push({ node, append: false });
      }
      return nodes;
    }
    function render() {
      if (!running || !config.enabled) return;
      for (const [host, refs] of mounted) if (!host.isConnected || !refs.box.isConnected) { refs.box.remove(); mounted.delete(host); }
      for (const anchor of anchors()) if (!mounted.has(anchor.node)) {
        const refs = build();
        if (anchor.append) anchor.node.append(refs.box);
        else anchor.node.before(refs.box);
        mounted.set(anchor.node, refs);
      }
      for (const refs of mounted.values()) {
        refs.box.hidden = config.requirePicker && !pickerReady();
        refs.heading.textContent = config.text.heading;
        refs.status.textContent = writing || !current.ready ? config.text.checking : current.selected && current.eligible ? config.text.selected : current.eligible ? pickerReady() ? config.text.unlocked : config.text.qualified : replace(config.text.remaining, 'amount', money(current.remainingCents));
        refs.progress.setAttribute('aria-label', config.text.progress);
        refs.progress.value = current.progress;
        refs.progress.hidden = !current.ready;
        refs.fine.textContent = config.text.finePrint;
        refs.picker.textContent = current.selected ? config.text.manage : config.text.picker;
        refs.picker.hidden = !current.ready || !current.eligible || !pickerReady();
        refs.picker.disabled = writing;
        refs.recommendation.textContent = config.text.recommendation;
        refs.recommendation.setAttribute('href', config.recommendationUrl || '/products/lume-stacker-slim-lume-bands');
        refs.recommendation.hidden = !current.ready || current.eligible || !config.recommendationUrl;
        refs.coupon.hidden = !config.couponEnabled;
        refs.input.setAttribute('aria-label', config.text.codeLabel);
        refs.input.placeholder = config.text.codeLabel;
        refs.input.readOnly = writing;
        refs.apply.textContent = config.text.codeApply;
        refs.apply.disabled = writing;
        refs.remove.textContent = config.text.codeRemove;
        refs.remove.disabled = writing;
        const codes = cart && activeCodes(cart);
        refs.summary.textContent = config.text.codeLabel + (codes && codes.length ? ' · ' + codes.join(', ') : '');
        refs.codes.textContent = codes && codes.length ? replace(config.text.codesLabel, 'codes', codes.join(', ')) : '';
        refs.remove.hidden = !codes || !codes.length;
        refs.help.textContent = config.text.codeHelp;
        refs.notice.textContent = message;
      }
    }
    function schedule() {
      if (!running || !config.enabled) return;
      loader.invalidate();
      current = model(null, config);
      render();
      win.clearTimeout(timer);
      timer = win.setTimeout(() => { if (!writing) loader.run(); }, config.debounceMs);
    }
    function stop() {
      running = false;
      version += 1;
      win.clearTimeout(timer);
      loader.invalidate();
      if (observer) observer.disconnect();
      if (win.RuneGiftBoxAdapter) win.RuneGiftBoxAdapter.stop();
      for (const [name, listener, options] of listeners) doc.removeEventListener(name, listener, options);
      listeners = [];
      for (const refs of mounted.values()) refs.box.remove();
      mounted.clear();
    }
    function start() {
      if (running || !config.enabled) return;
      running = true;
      if (win.RuneGiftBoxAdapter) win.RuneGiftBoxAdapter.start();
      for (const name of config.refreshEvents) { doc.addEventListener(name, schedule); listeners.push([name, schedule]); }
      const onFrameLoad = event => { if (event.target && event.target.tagName === 'IFRAME') render(); };
      doc.addEventListener('load', onFrameLoad, true);
      listeners.push(['load', onFrameLoad, true]);
      const onCartInteraction = event => {
        const target = event.target && event.target.closest ? event.target : null;
        if (!target || !target.closest('#sidebar-cart, #shopify-section-cart-template')) return;
        if (event.type === 'click' && target.closest('[data-action="update-item-quantity"], [data-action="remove-item"]') || event.type === 'change' && target.matches('.QuantitySelector__CurrentQuantity')) schedule();
      };
      for (const name of ['click', 'change']) { doc.addEventListener(name, onCartInteraction, true); listeners.push([name, onCartInteraction, true]); }
      observer = new win.MutationObserver(records => {
        let pickerChanged = false;
        if (config.pickerSelector) {
          try {
            pickerChanged = records.some(record => [...record.addedNodes, ...record.removedNodes].some(node => node.nodeType === 1 && (node.matches(config.pickerSelector) || node.querySelector(config.pickerSelector))));
          } catch (_) { pickerChanged = false; }
        }
        const relevant = records.some(record => {
          if (record.target.nodeType === 1 && record.target.closest('[data-rune-gift-offer]')) return false;
          const changed = [...record.addedNodes, ...record.removedNodes];
          if (changed.length && changed.every(node => node.nodeType === 1 && node.matches('[data-rune-gift-offer]'))) return false;
          return record.target.nodeType === 1 && record.target.closest('#sidebar-cart, #shopify-section-cart-template') || changed.some(node => node.nodeType === 1 && (node.matches('.Cart, #sidebar-cart, #shopify-section-cart-template') || node.querySelector('.Cart, #sidebar-cart, #shopify-section-cart-template')));
        });
        if (relevant) { render(); schedule(); }
        else if (pickerChanged) render();
      });
      observer.observe(doc.body, { childList: true, subtree: true });
      render();
      loader.run();
    }
    return {
      start,
      stop,
      refresh: schedule,
      invalidate() { loader.invalidate(); current = model(null, config); render(); },
      configure(next) { stop(); config = merge({ ...config, ...next, text: { ...config.text, ...(next && next.text) } }); win.RuneGiftOffer = config; cart = null; current = model(null, config); start(); },
      getState() { return { enabled: config.enabled, writing, offer: { ...current } }; }
    };
  }
  return { defaults, designatedGift, enteredCodes, activeCodes, discountPayload, model, latestLoader, createController };
});
